import { useEffect, useState } from 'react';
import { settingsApi, targetsApi, formatCurrency, formatCurrencyWhole } from '../utils/api';
import { periodKeyLabel, periodNoun } from '../utils/targetPeriodLabels';
import { formatDateShort, formatDateWithWeekday } from '../utils/dateFormat';
import { categoryLineColor } from '../utils/classificationColors';
import { granularitySwitchDialog } from '../utils/confirmCopy';
import ConfirmModal from './ConfirmModal';
import InfoNote from './InfoNote';
import ExpandCaret from './ExpandCaret';
import Icon from './Icon';

// יעדי תקציב — the target half of what used to be one CategoryLineSheet. Line
// IDENTITY (create/rename/recolour/delete) lives in CategoryLineDefinitionSheet;
// this card owns the amounts, the target settings, and the annual coverage
// strip. The rules the old sheet rested on survive the split verbatim:
//
//   SUGGESTIONS ARE PLACEHOLDERS, NEVER VALUES. A row prefilled with last
//   period's actual as a real value would be saved on the next submit whether
//   or not anyone looked at it.
//
//   AMOUNTS ARE EX-VAT, ALWAYS. There is no basis to pick — the sheet says so
//   in the column headers and above the table, and the server stores nothing else.
//
//   SETTINGS BEFORE THE BATCH. Saving targets under a granularity the settings
//   do not know about means the next load reads the other series.

// Said in the column headers, and once in full above the table.
const EX_VAT = 'ללא מע״מ';

const rowKey = (r) => `line:${r.category_line_id}`;

// "מיום א׳ 30/08/26 עד יום שבת 05/09/26 (כולל)" — the exact days a reach
// option covers, weekday first because that is how a restaurant thinks of a
// week. Every option ends with the year except "this period only": the
// server's carry-forward stops there, and this line is where the sheet says
// so. The range itself comes from the server (the Sunday–Saturday week bounds
// live there, and the client keeps no copy).
function ReachDates({ reach, mode }) {
  const range = reach?.[mode];
  if (!range) return null;
  return (
    <span className="sheet-reach-dates">
      {`מ${formatDateWithWeekday(range.from)} עד ${formatDateWithWeekday(range.to)} (כולל)`}
      {mode !== 'this_period' && ' · עד סוף השנה'}
    </span>
  );
}

function parseAmount(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : false;
}

// ── the annual coverage strip ───────────────────────────────────────────────
// Every period of the current year in one glance: explicitly set / carried
// forward / nothing. Clicking an uncovered period jumps the editor to it.
const STRIP_STYLE = {
  explicit:  { background: '#3f6b5c', border: '1px solid #3f6b5c' },
  inherited: { background: '#d9e6df', border: '1px solid #9fbcae' },
  none:      { background: '#fff', border: '1px dashed #cbd5e1' },
};

function CoverageStrip({ coverage, editingKey, onJump }) {
  if (!coverage || !coverage.keys?.length) return null;
  const { period, keys } = coverage;
  const label = (key) => periodKeyLabel(key, period);
  return (
    <div style={{ margin: '2px 0 12px' }}>
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }} role="listbox" aria-label="כיסוי יעדים שנתי">
        {keys.map(({ key, state }) => (
          <button
            key={key}
            type="button"
            role="option"
            aria-selected={key === editingKey}
            aria-label={`${label(key)} — ${state === 'explicit' ? 'יעד הוגדר' : state === 'inherited' ? 'יעד ממשיך' : 'אין יעד'}`}
            title={`${label(key)} · ${state === 'explicit' ? 'יעד הוגדר' : state === 'inherited' ? 'יעד ממשיך מתקופה קודמת' : 'אין יעד'}`}
            onClick={onJump ? () => onJump(key) : undefined}
            aria-disabled={!onJump}
            style={{
              ...STRIP_STYLE[state],
              width: period === 'month' ? 22 : 9,
              height: 14,
              borderRadius: 3,
              padding: 0,
              cursor: onJump ? 'pointer' : 'default',
              outline: key === editingKey ? '2px solid #1a1a2e' : 'none',
              outlineOffset: 1,
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 11.5, color: 'var(--ink-muted)' }}>
        <span><span style={{ ...STRIP_STYLE.explicit, display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: 'middle', marginInlineEnd: 4 }} />יעד הוגדר</span>
        <span><span style={{ ...STRIP_STYLE.inherited, display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: 'middle', marginInlineEnd: 4 }} />יעד ממשיך</span>
        <span><span style={{ ...STRIP_STYLE.none, display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: 'middle', marginInlineEnd: 4 }} />אין יעד</span>
      </div>
    </div>
  );
}

export default function BudgetTargetsSheet({
  summary, settings, onSaved, onClose, open = true, onToggle, onJumpToPeriod,
}) {
  const periodLabel = periodKeyLabel(summary.period_key, summary.period);

  // Collapsed, the header still has to answer "what is my budget" — otherwise
  // folding the section away just hides the answer behind a chevron.
  const lineCount = (summary.lines || []).length;
  const budgetTotal = summary.rollup?.budget_total || 0;
  const headerSummary = lineCount === 0 ? ''
    : budgetTotal > 0
      ? `סך היעדים ${formatCurrencyWhole(budgetTotal)} · ${periodLabel}`
      : 'טרם נקבעו יעדים';

  const [amounts, setAmounts] = useState({});
  const [period, setPeriod] = useState(summary.period);
  const [reach, setReach] = useState('forward');
  // The reach options, their ⓘ note and the date range all describe the
  // granularity the sheet is about to SAVE INTO — the picker's, not the
  // loaded summary's. Under "סכום לשבוע", "this period" is this week.
  const noun = periodNoun(period);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rowError, setRowError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [coverage, setCoverage] = useState(null);

  // Not an edit history — spend_targets is upserted in place, so a changed
  // amount leaves no trace of what it used to be (see the routes' upsert).
  // This is a flat list of every target row THAT CURRENTLY EXISTS — the same
  // information the coverage strip already draws as a calendar, here as a
  // table. Same idiom as "פדיון שכבר הוזן" on הכנסות מהקופה: its own
  // collapsible, collapsed by default.
  //
  // Filtered to the restaurant's CURRENT granularity (settings.period), not
  // every row the table happens to hold: switching month↔week leaves the old
  // series' rows behind (they simply stop being read — see the "SETTINGS
  // BEFORE THE BATCH" note on submit below), and a list mixing months and
  // weeks reads as a data-integrity bug even though the underlying rows are
  // legitimate leftovers.
  const [entriesOpen, setEntriesOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  useEffect(() => {
    if (!open || !entriesOpen) return;
    let stale = false;
    setHistoryLoading(true);
    setHistoryError('');
    targetsApi.list({ scope: 'budget_line', period: settings.period })
      .then(r => { if (!stale) setHistory(r.data || []); })
      .catch(() => { if (!stale) setHistoryError('שגיאה בטעינת רשימת היעדים'); })
      .finally(() => { if (!stale) setHistoryLoading(false); });
    return () => { stale = true; };
  }, [open, entriesOpen, settings.period]);

  // Editing/deleting a history row acts on that ONE row directly (PUT/DELETE
  // /api/targets/:id) — unlike the sheet's own per-category inputs above,
  // which always write through the reach-aware upsert. A history row can
  // belong to any period, past or future, so there is no "reach" here: this
  // is a point fix to exactly the row shown.
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [editingAmount, setEditingAmount] = useState('');
  const [historyRowBusy, setHistoryRowBusy] = useState(null);   // id currently saving/deleting
  const [historyActionError, setHistoryActionError] = useState('');

  const startEditHistory = (row) => {
    setHistoryActionError('');
    setEditingHistoryId(row.id);
    setEditingAmount(String(row.amount));
  };
  const cancelEditHistory = () => { setEditingHistoryId(null); setEditingAmount(''); };

  const saveEditHistory = async (row) => {
    const parsed = parseAmount(editingAmount);
    if (parsed === false || parsed === null) {
      setHistoryActionError('סכום היעד חייב להיות מספר גדול מאפס');
      return;
    }
    setHistoryRowBusy(row.id);
    setHistoryActionError('');
    try {
      await targetsApi.update(row.id, { amount: parsed });
      setHistory(list => list.map(h => (h.id === row.id ? { ...h, amount: parsed } : h)));
      setEditingHistoryId(null);
      // Awaited — the row edited might be the CURRENT period, and clearing
      // historyRowBusy before this resolves would re-enable the row's
      // buttons while the coverage strip above is still showing pre-edit
      // numbers.
      await onSaved?.();
    } catch (err) {
      setHistoryActionError(err?.response?.data?.error || 'שגיאה בעדכון היעד');
    } finally {
      setHistoryRowBusy(null);
    }
  };

  const deleteHistory = (row) => {
    setConfirm({
      title: 'מחיקת יעד',
      body: [
        `${row.category_line_name || '—'} · ${periodKeyLabel(row.period_key, row.period)} · ${formatCurrency(row.amount)}`,
        // NOT keyed on row.carry_forward — resolveTarget (spendTargets.js)
        // inherits from the nearest EARLIER period whose OWN row carries
        // forward, regardless of what this row's flag is. A one-off March
        // override sitting on top of a carrying January target still falls
        // back to January once deleted, even though March itself was
        // carry_forward=0 — so the message has to describe the real
        // mechanism, not read the deleted row's own flag.
        'אם יש יעד קודם שממשיך לתקופה זו, היא תחזור אליו. אחרת היא תישאר ללא יעד.',
      ],
      confirmLabel: 'מחיקת היעד',
      cancelLabel: 'ביטול',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        setHistoryRowBusy(row.id);
        setHistoryActionError('');
        try {
          await targetsApi.remove(row.id);
          setHistory(list => list.filter(h => h.id !== row.id));
          await onSaved?.();
        } catch (err) {
          setHistoryActionError(err?.response?.data?.error || 'שגיאה במחיקת היעד');
        } finally {
          setHistoryRowBusy(null);
        }
      },
    });
  };

  // The strip is a read of spend_targets, refetched whenever a save lands
  // (summary identity changes), the card opens, or the granularity picker
  // moves. It follows the granularity the sheet is ABOUT TO SAVE INTO, and
  // the same call carries the reach date ranges for that key — the batch
  // route defaults to the current period when the granularity differs from
  // the summary's, so the key is sent only when it applies (see submit).
  useEffect(() => {
    if (!open) return;
    let stale = false;
    targetsApi.coverage({
      period,
      ...(period === summary.period ? { period_key: summary.period_key } : {}),
    })
      .then(r => { if (!stale) setCoverage(r.data); })
      .catch(() => { if (!stale) setCoverage(null); });
    return () => { stale = true; };
  }, [open, summary, period]);
  // Only a payload for the granularity the picker holds is shown: between a
  // switch and its refetch, the old one would put month dates under week wording.
  const coverageForPeriod = coverage?.period === period ? coverage : null;
  // Jumping loads the page's summary by key, and the page's granularity is the
  // SUMMARY's — a weekly key would make that route fall back to the current
  // month without saying so. So the strip is read-only while the two differ.
  const canJump = period === summary.period;

  const rows = summary.lines || [];
  const unmapped = summary.unmapped;

  const suggestionFor = (r) => (r.prev_actual_net > 0 ? Math.round(r.prev_actual_net) : null);

  const effective = (r) => {
    const key = rowKey(r);
    if (!(key in amounts)) return r.target;
    const parsed = parseAmount(amounts[key]);
    return parsed === false ? r.target : parsed;
  };

  const changed = rows.filter(r => {
    const key = rowKey(r);
    if (!(key in amounts)) return false;
    const parsed = parseAmount(amounts[key]);
    if (parsed === false) return false;
    return parsed !== r.target;
  });

  const total = rows.reduce((n, r) => n + (effective(r) || 0), 0);

  const set = (key, value) => { setRowError(null); setAmounts(prev => ({ ...prev, [key]: value })); };

  const fillFromPrevious = () => {
    setRowError(null);
    const next = { ...amounts };
    rows.forEach(r => {
      const prev = suggestionFor(r);
      if (prev != null) next[rowKey(r)] = String(prev);
    });
    setAmounts(next);
  };

  const changePeriod = (next) => {
    if (next === period) return;
    const dialog = granularitySwitchDialog(period, next, settings.target_counts);
    if (!dialog) { setPeriod(next); return; }
    setConfirm({ ...dialog, onConfirm: () => { setConfirm(null); setPeriod(next); } });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setRowError(null);

    const payload = [];
    const payloadRow = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const key = rowKey(r);
      if (!(key in amounts)) continue;
      const parsed = parseAmount(amounts[key]);
      if (parsed === false) { setRowError(i); setError('סכום היעד חייב להיות מספר גדול מאפס'); return; }
      if (parsed === r.target) continue;
      if (parsed === null && r.target == null) continue;
      payload.push({ scope: 'budget_line', category_line_id: r.category_line_id, amount: parsed });
      payloadRow.push(i);
    }

    setSaving(true);
    try {
      // Settings first: saving under a granularity the settings do not know
      // about means the next load reads the other series and these are invisible.
      if (period !== settings.period) {
        await settingsApi.updateTargets({ period });
      }
      if (payload.length > 0) {
        await targetsApi.saveBatch({
          period,
          ...(period === summary.period ? { period_key: summary.period_key } : {}),
          apply_to: reach, rows: payload,
        });
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data.row_index === 'number' && payloadRow[data.row_index] != null) {
        setRowError(payloadRow[data.row_index]);
      }
      setError(data?.error || 'שגיאה בשמירת היעדים');
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (r, i) => {
    const key = rowKey(r);
    const suggestion = suggestionFor(r);
    return (
      <tr key={key} style={rowError === i ? { background: '#fff1f2' } : undefined}>
        <td>
          <span style={{
            display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
            background: r.color || categoryLineColor(r, i), verticalAlign: 'middle',
          }} />
        </td>
        <td>{r.category_line_name}</td>
        <td style={{ color: 'var(--ink-muted)', whiteSpace: 'nowrap' }}>
          {formatCurrency(r.actual_net)}
        </td>
        <td>
          <input
            className="form-control" type="number" min="0" step="1" inputMode="decimal"
            aria-label={`יעד עבור ${r.category_line_name} — ${EX_VAT}`}
            value={key in amounts ? amounts[key] : (r.target != null ? String(r.target) : '')}
            onChange={e => set(key, e.target.value)}
            placeholder={suggestion != null ? `קודם ${suggestion.toLocaleString()}` : '—'}
            style={{ padding: '4px 8px' }}
          />
        </td>
      </tr>
    );
  };

  return (
    <>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <section className={`card category-lines-card${open ? '' : ' is-collapsed'}`}>
        <button
          type="button" className="category-lines-toggle"
          onClick={onToggle} aria-expanded={open} aria-controls="budget-targets-body"
        >
          <span className="category-lines-title">יעדי תקציב</span>
          <span className="category-lines-summary">{headerSummary}</span>
          <ExpandCaret open={open} size={14} />
        </button>

        {open && (
        <form id="budget-targets-body" dir="rtl" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column' }}>

          {error && (
            <div className="badge-red" style={{ display: 'block', marginBottom: 12, padding: '8px 12px', borderRadius: 8 }}>
              {error}
            </div>
          )}

          <CoverageStrip
            coverage={coverageForPeriod}
            editingKey={coverageForPeriod?.period_key}
            onJump={canJump ? onJumpToPeriod : undefined}
          />

          {/* The rule for the whole card, stated before anyone starts typing.
              The column headers repeat it where the digits go; this says it once
              in a full sentence, which is what a first-time setup needs. */}
          <p className="sheet-settings-sub" style={{ margin: '10px 0 6px' }}>
            כל הסכומים כאן — היעד והרכש בפועל — הם {EX_VAT}.
          </p>

          <div className="table-wrapper sheet-table" style={{ overflowY: 'auto', maxHeight: 338 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34 }} />
                  <th>קטגוריה</th>
                  <th style={{ whiteSpace: 'nowrap' }}>
                    רכש · {periodLabel}
                    <span style={{ display: 'block', fontWeight: 400, fontSize: 11.5, color: '#94a3b8' }}>{EX_VAT}</span>
                  </th>
                  <th style={{ width: 170, whiteSpace: 'nowrap' }}>
                    יעד · {periodLabel}
                    <span style={{ display: 'block', fontWeight: 400, fontSize: 11.5, color: '#94a3b8' }}>{EX_VAT}</span>
                    {/* A bulk action ON this column, so it lives in this
                        column's header. In the toolbar above the table it read
                        as a page-level action and said nothing about what it
                        would fill. */}
                    {rows.length > 0 && (
                      <button type="button" className="sheet-th-fill" onClick={fillFromPrevious}>
                        מלא לפי התקופה הקודמת
                      </button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => renderRow(r, i))}
                {rows.length === 0 && (
                  <tr><td colSpan={4} style={{ color: '#94a3b8', padding: '14px 8px' }}>
                    עדיין אין קטגוריות. הוסיפו אותם במקטע "הגדרת קטגוריות" למעלה.
                  </td></tr>
                )}
              </tbody>
              {/* The total belongs UNDER the column it totals — floating in the
                  toolbar it was a number with no column to belong to, and it
                  had to repeat the VAT basis the header already states. */}
              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={3}>סה״כ יעדים</td>
                    <td className="sheet-total-amount">{formatCurrency(total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* The unmapped bucket, deliberately NOT a row: it cannot carry a
              target, and a disabled input in the table would invite the attempt.
              It is context — and the number this screen exists to shrink. */}
          {unmapped && unmapped.actual_net > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#b45309' }}>
              {formatCurrency(unmapped.actual_net)} מהרכש
              עדיין לא משויך לאף קטגוריה — שייכו סיווגים או ספקים כדי לכלול אותו.
            </p>
          )}

          <fieldset className="sheet-settings" style={{ marginTop: 14 }}>
            <legend>הגדרות היעד</legend>
            <p className="sheet-settings-sub">
              חל על כל הקטגוריות ועל כל התקופות · נשמר כהגדרה של המסעדה
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <InfoNote
                id="bl-period" label="יחידת היעד" triggerLabel="מה זו יחידת היעד"
                style={{ flex: '1 1 230px' }}
                note={`הסכום שתקלידו בכל שורה הוא לחודש אחד או לשבוע אחד. הבחירה קובעת
                  גם מול איזו תקופה נמדד הרכש בפועל. סכומים לא מומרים בין השניים.`}
              >
                <select id="bl-period" className="form-control" value={period} onChange={e => changePeriod(e.target.value)}>
                  <option value="month">סכום לחודש</option>
                  <option value="week">סכום לשבוע</option>
                </select>
              </InfoNote>
            </div>

            <div className="sheet-reach">
              <InfoNote
                id="bl-reach" label="שמירה זו חלה על" triggerLabel="על אילו תקופות תחול השמירה"
                style={{ flex: '1 1 100%', minWidth: 0 }}
                note={(
                  <>
                    <p style={{ margin: '0 0 6px' }}>
                      בחירה חד־פעמית — לא נשמרת כהגדרה, ובפעם הבאה שתפתחו את המסך היא
                      תחזור לבררת המחדל. כל יעד נעצר בסוף השנה: בינואר קובעים מחדש.
                    </p>
                    <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                      <li><b>{`מ${noun.thisOne} והלאה`}</b> — {`מ${noun.thisOne} עד סוף השנה. ${noun.plural} שאין להם יעד משלהם יורשים את הסכום.`}</li>
                      <li><b>{`מ${noun.nextOne} והלאה`}</b> — {`אותו דבר, אבל מתחיל ${noun.nextOne}; ${noun.thisOne} לא משתנה.`}</li>
                      <li><b>{`רק ${noun.thisOne}`}</b> — {`חד פעמי: ${noun.thisOne} בלבד, ואחריו חוזרים ליעד הקודם אם היה.`}</li>
                      <li><b>כל השנה</b> — {`מחליף את היעד בכל ${noun.ofYear}, כולל אלה שכבר עברו.`}</li>
                    </ul>
                  </>
                )}
              >
                <select id="bl-reach" className="form-control" value={reach} onChange={e => setReach(e.target.value)}>
                  <option value="forward">{`מ${noun.thisOne} והלאה`}</option>
                  <option value="next_forward">{`מ${noun.nextOne} והלאה`}</option>
                  <option value="this_period">{`רק ${noun.thisOne} — חד פעמי`}</option>
                  <option value="year">כל השנה, כולל תקופות שכבר עברו</option>
                </select>
                <ReachDates reach={coverageForPeriod?.reach} mode={reach} />
              </InfoNote>
            </div>
          </fieldset>

          {reach === 'year' && (
            <div style={{ fontSize: 12.5, color: '#b45309', marginBottom: 10 }}>
              יעדים קיימים בשנה זו, כולל תקופות שכבר עברו, יוחלפו.
            </div>
          )}

          {/* Sticky ONLY while something is unsaved. Inline, a save button can
              scroll out of sight and take the edit with it — the one real thing
              a modal was doing for us. With nothing pending it returns to the
              flow rather than parking a bar over the list below. */}
          <div className={`modal-actions${changed.length ? ' category-lines-actions-stuck' : ''}`}
            style={{ alignItems: 'center' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'שומר…' : 'שמירה'}
            </button>
            {changed.length > 0 && (
              <span style={{ marginInlineStart: 'auto', fontSize: 12.5, color: 'var(--ink-muted)' }}>
                {changed.length === 1 ? 'שורה אחת תשתנה' : `${changed.length} שורות ישתנו`}
              </span>
            )}
          </div>

          {/* NOT an edit history (see the note above the fetch effect) —
              every CURRENT target row, across every category line and period
              of this restaurant's active granularity. Its own collapsible,
              closed by default, same idiom as "פדיון שכבר הוזן" on הכנסות
              מהקופה: a record to check, not something every visit needs open. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
            <button
              type="button"
              onClick={() => setEntriesOpen(o => !o)}
              aria-expanded={entriesOpen} aria-controls="budget-targets-history"
              style={{
                display: 'flex', alignItems: 'center', gap: 6, background: 'none',
                border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
              }}
            >
              <ExpandCaret open={entriesOpen} size={13} />
              <h3 style={{ margin: 0, fontSize: 14 }}>
                יעדים שכבר נקבעו{history.length > 0 ? ` (${history.length})` : ''}
              </h3>
            </button>
            {entriesOpen && (
              <div id="budget-targets-history" style={{ marginTop: 10 }}>
                {(historyError || historyActionError) && (
                  <div className="badge-red" style={{ display: 'block', marginBottom: 12, padding: '8px 12px', borderRadius: 8 }}>
                    {historyError || historyActionError}
                  </div>
                )}
                {historyLoading ? (
                  <p style={{ color: '#94a3b8', fontSize: 13 }}>טוען…</p>
                ) : history.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13 }}>עדיין לא נקבעו יעדים.</p>
                ) : (
                  <div className="table-wrapper sheet-table" style={{ overflowY: 'auto', maxHeight: 300 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>קטגוריה</th>
                          <th>תקופה</th>
                          <th>יעד ({EX_VAT})</th>
                          <th>תחולה</th>
                          {/* "עודכן" not "הוזן": a target is upserted in
                              place, so this column has to name whoever last
                              CHANGED the number. Saying "הוזן ע״י" credited
                              the original author for an amount someone else
                              typed. On a row nobody has edited the two are
                              the same person, so nothing reads oddly there. */}
                          <th>עודכן ע״י</th>
                          <th style={{ width: 84 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {history.map(h => {
                          const isEditing = editingHistoryId === h.id;
                          const isBusy = historyRowBusy === h.id;
                          return (
                            <tr key={h.id}>
                              <td>{h.category_line_name || '—'}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{periodKeyLabel(h.period_key, h.period)}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {isEditing ? (
                                  <input
                                    className="form-control" type="number" min="0" step="1" autoFocus
                                    aria-label={`עריכת יעד — ${h.category_line_name || ''} · ${periodKeyLabel(h.period_key, h.period)}`}
                                    value={editingAmount}
                                    onChange={e => setEditingAmount(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { e.preventDefault(); saveEditHistory(h); }
                                      if (e.key === 'Escape') cancelEditHistory();
                                    }}
                                    style={{ width: 110, padding: '4px 8px' }}
                                  />
                                ) : formatCurrency(h.amount)}
                              </td>
                              <td style={{ color: 'var(--ink-muted)' }}>{h.carry_forward ? 'נמשך קדימה' : 'חד פעמי'}</td>
                              <td style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                                {h.updated_by_email || h.created_by_email || '—'}
                                {h.updated_at || h.created_at
                                  ? ` · ${formatDateShort(h.updated_at || h.created_at)}`
                                  : ''}
                              </td>
                              <td style={{ whiteSpace: 'nowrap', padding: '6px 4px' }}>
                                {isEditing ? (
                                  <>
                                    <button type="button" className="btn btn-secondary"
                                      aria-label="שמירת היעד" title="שמירה" disabled={isBusy}
                                      onClick={() => saveEditHistory(h)}
                                      style={{ padding: '4px 7px', marginInlineEnd: 4 }}>
                                      <Icon name="check" size={13} />
                                    </button>
                                    <button type="button" className="btn btn-secondary"
                                      aria-label="ביטול העריכה" title="ביטול" disabled={isBusy}
                                      onClick={cancelEditHistory}
                                      style={{ padding: '4px 7px' }}>
                                      <Icon name="close" size={13} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button type="button" className="btn btn-secondary"
                                      aria-label={`עריכת יעד ${h.id}`} title="עריכה" disabled={isBusy}
                                      onClick={() => startEditHistory(h)}
                                      style={{ padding: '4px 7px', marginInlineEnd: 4 }}>
                                      <Icon name="edit" size={13} />
                                    </button>
                                    <button type="button" className="btn btn-secondary"
                                      aria-label={`מחיקת יעד ${h.id}`} title="מחיקה" disabled={isBusy}
                                      onClick={() => deleteHistory(h)}
                                      style={{ padding: '4px 7px', color: '#b91c1c' }}>
                                      <Icon name="trash" size={13} />
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
        )}
      </section>
    </>
  );
}
